// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  FileInputButton,
  FileInputButtonHandle,
} from '@/components/file-input-button';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { UploadErrorDialog } from '@/components/upload-error-dialog';
import { UploadFileOutlined } from '@/constants/hardfin-standard-icons';
import { isFileReadError } from '@/file/api/is-file-read-error';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getTicketFiles } from '@/ticket/api/get-ticket-files';
import { uploadTicketFile } from '@/ticket/api/upload-ticket-file';
import { TicketFileTable } from '@/ticket/components/ticket-files-list';
import { useTicket } from '@/ticket/hooks/use-ticket';
import { Stack } from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useRef } from 'react';

export const Route = createFileRoute('/_app/tickets/$ticketKey/attachments')({
  component: TicketAttachmentsRoute,
});

function TicketAttachmentsRoute() {
  const queryClient = useQueryClient();
  const { ticketKey } = Route.useParams();
  const { ticket } = useTicket(ticketKey);
  const fileInputRef = useRef<FileInputButtonHandle>(null);
  const uploadErrorDialog = useDisclosure();

  const { data } = useSuspenseQuery({
    queryKey: ['ticket', ticketKey, 'files'],
    queryFn: () => getTicketFiles({ key: ticketKey }),
  });

  const uploadFileMutation = useMutation({
    mutationFn: (file: File) => uploadTicketFile(ticket.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ticket', ticketKey],
      });
    },
    onError: (error) => {
      if (isFileReadError(error)) {
        uploadErrorDialog.open();
      }
    },
  });

  return (
    <>
      <UploadErrorDialog
        isOpen={uploadErrorDialog.isOpen}
        onClose={uploadErrorDialog.close}
        onRetry={() => {
          uploadErrorDialog.close();
          fileInputRef.current?.openFilePicker();
        }}
      />
      <HardfinTabCardWrapper
        title="Attachments"
        actions={
          <FileInputButton
            ref={fileInputRef}
            size="small"
            onChange={uploadFileMutation.mutate}
            disabled={uploadFileMutation.isPending}
            startIcon={<UploadFileOutlined fontSize="small" />}
          >
            Upload file
          </FileInputButton>
        }
      >
        <Stack paddingTop={2}>
          <TicketFileTable ticketKey={ticketKey} files={data || []} />
        </Stack>
      </HardfinTabCardWrapper>
    </>
  );
}
